/**
 * Indian financial year helpers.
 *
 * The FY runs 1 April → 31 March. FY quarters are Q1 = Apr–Jun, Q2 = Jul–Sep,
 * Q3 = Oct–Dec, Q4 = Jan–Mar. An FY is labelled by the calendar year it starts
 * in, so 1 Apr 2026 → 31 Mar 2027 is "FY 2026-27" and its `startYear` is 2026.
 *
 * Everything here operates on `IsoDate` — a plain `YYYY-MM-DD` string, never a
 * `Date`. Accounting dates are calendar dates, not instants: parsing
 * "2026-03-31" into a `Date` and formatting it back can shift the day across a
 * timezone boundary and silently move a voucher into the wrong financial year.
 * Strings in, strings out, no timezone anywhere in the pipeline.
 */

/** A calendar date with no time and no timezone, formatted `YYYY-MM-DD`. */
export type IsoDate = string

export type FiscalQuarter = 1 | 2 | 3 | 4

export interface DateRange {
  /** Inclusive. */
  from: IsoDate
  /** Inclusive. */
  to: IsoDate
}

export interface FiscalYear {
  /** Calendar year the FY starts in. FY 2026-27 → 2026. */
  startYear: number
  /** 1 April of `startYear`. */
  start: IsoDate
  /** 31 March of `startYear + 1`. */
  end: IsoDate
  /** Display label, e.g. "FY 2026-27". */
  label: string
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

interface DateParts {
  year: number
  /** 1-12. */
  month: number
  /** 1-31. */
  day: number
}

/** Number of days in a given month, honouring leap years. */
function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one. UTC keeps it stable.
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/**
 * Splits an `IsoDate` into its parts, rejecting anything that is not a real
 * calendar date. Throws rather than silently coercing, because every caller is
 * on a path where a wrong date means a wrong number in a financial report.
 */
export function parseIsoDate(date: IsoDate): DateParts {
  const match = ISO_DATE_RE.exec(date)
  if (!match) {
    throw new RangeError(`Invalid date "${date}": expected YYYY-MM-DD`)
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  if (month < 1 || month > 12) {
    throw new RangeError(`Invalid date "${date}": month must be 01-12`)
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    throw new RangeError(`Invalid date "${date}": day out of range for that month`)
  }
  return { year, month, day }
}

/** Returns true for a well-formed, real calendar date. */
export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string') return false
  try {
    parseIsoDate(value)
    return true
  } catch {
    return false
  }
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/** Builds an `IsoDate` from parts. Month is 1-12. */
export function toIsoDate(year: number, month: number, day: number): IsoDate {
  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`
}

/**
 * Today in the given IANA timezone, as an `IsoDate`.
 *
 * Defaults to Asia/Kolkata rather than the browser's zone: "today" in an
 * accounting sense is the company's local day. A user travelling abroad should
 * still book a voucher against the Indian date, and a report run at 02:00 IST
 * must not silently use yesterday because the machine is on UTC.
 */
export function today(timeZone = 'Asia/Kolkata'): IsoDate {
  // en-CA formats as YYYY-MM-DD, which is exactly the shape we want.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

/** Chronological comparison. Safe as a lexicographic compare given the format. */
export function compareIsoDate(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** Inclusive on both ends. */
export function isWithin(date: IsoDate, range: DateRange): boolean {
  return date >= range.from && date <= range.to
}

/** Adds (or subtracts, for negative `days`) whole days. */
export function addDays(date: IsoDate, days: number): IsoDate {
  const { year, month, day } = parseIsoDate(date)
  const shifted = new Date(Date.UTC(year, month - 1, day + days))
  return toIsoDate(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate())
}

/**
 * Adds whole months, clamping the day to the end of the target month so that
 * 31 Jan + 1 month is 28/29 Feb rather than rolling into March.
 */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const { year, month, day } = parseIsoDate(date)
  const zeroBased = year * 12 + (month - 1) + months
  const targetYear = Math.floor(zeroBased / 12)
  const targetMonth = (zeroBased % 12) + 1
  return toIsoDate(targetYear, targetMonth, Math.min(day, daysInMonth(targetYear, targetMonth)))
}

/** Whole days from `from` to `to`. Negative when `to` precedes `from`. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const a = parseIsoDate(from)
  const b = parseIsoDate(to)
  const ms =
    Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)
  return Math.round(ms / 86_400_000)
}

/** The calendar year an FY starts in, for the FY containing `date`. */
export function fiscalYearStartYear(date: IsoDate): number {
  const { year, month } = parseIsoDate(date)
  // Jan/Feb/Mar belong to the FY that started the previous April.
  return month >= 4 ? year : year - 1
}

/** Formats an FY label from its start year: 2026 → "FY 2026-27". */
export function fiscalYearLabel(startYear: number): string {
  return `FY ${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`
}

/** The full financial year that contains `date`. */
export function fiscalYearOf(date: IsoDate): FiscalYear {
  return fiscalYearFromStartYear(fiscalYearStartYear(date))
}

/** Builds an FY from the calendar year it starts in. */
export function fiscalYearFromStartYear(startYear: number): FiscalYear {
  return {
    startYear,
    start: toIsoDate(startYear, 4, 1),
    end: toIsoDate(startYear + 1, 3, 31),
    label: fiscalYearLabel(startYear),
  }
}

/** The FY quarter (1-4) that `date` falls in. Q1 is Apr–Jun. */
export function fiscalQuarterOf(date: IsoDate): FiscalQuarter {
  const { month } = parseIsoDate(date)
  // Shift April to index 0, then bucket into three-month groups.
  const monthsIntoFy = (month - 4 + 12) % 12
  return ((monthsIntoFy / 3) | 0) + 1 as FiscalQuarter
}

/** Inclusive date range of one FY quarter. */
export function fiscalQuarterRange(startYear: number, quarter: FiscalQuarter): DateRange {
  const firstMonthOffset = (quarter - 1) * 3
  const startMonthZeroBased = 3 + firstMonthOffset // April is month index 3.
  const startCalYear = startYear + Math.floor(startMonthZeroBased / 12)
  const startMonth = (startMonthZeroBased % 12) + 1

  const endMonthZeroBased = startMonthZeroBased + 2
  const endCalYear = startYear + Math.floor(endMonthZeroBased / 12)
  const endMonth = (endMonthZeroBased % 12) + 1

  return {
    from: toIsoDate(startCalYear, startMonth, 1),
    to: toIsoDate(endCalYear, endMonth, daysInMonth(endCalYear, endMonth)),
  }
}

/** All four quarter ranges of an FY, in order. */
export function fiscalQuarters(startYear: number): Array<DateRange & { quarter: FiscalQuarter; label: string }> {
  return ([1, 2, 3, 4] as const).map((quarter) => ({
    quarter,
    label: `Quarter ${quarter}`,
    ...fiscalQuarterRange(startYear, quarter),
  }))
}

/** Inclusive range of the calendar month containing `date`. */
export function monthRange(date: IsoDate): DateRange {
  const { year, month } = parseIsoDate(date)
  return { from: toIsoDate(year, month, 1), to: toIsoDate(year, month, daysInMonth(year, month)) }
}

/** The identifiers behind the date-range preset dropdown. */
export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_financial_year'
  | 'last_financial_year'
  | 'all_time'

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This Week',
  this_month: 'This Month',
  last_month: 'Last Month',
  this_quarter: 'This Quarter',
  last_quarter: 'Last Quarter',
  this_financial_year: 'This Financial Year',
  last_financial_year: 'Last Financial Year',
  all_time: 'All Time',
}

/** Earliest date the app will ever query. Stands in for "no lower bound". */
export const MIN_DATE: IsoDate = '1900-01-01'

/**
 * Resolves a preset to a concrete inclusive range.
 *
 * `asOf` defaults to today in Indian time and is injectable so tests never
 * depend on the wall clock.
 */
export function resolveDateRangePreset(
  preset: DateRangePreset,
  asOf: IsoDate = today(),
): DateRange {
  switch (preset) {
    case 'today':
      return { from: asOf, to: asOf }

    case 'yesterday': {
      const y = addDays(asOf, -1)
      return { from: y, to: y }
    }

    case 'this_week': {
      // Weeks run Monday → Sunday, the convention Indian businesses use.
      const { year, month, day } = parseIsoDate(asOf)
      const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay() // 0 = Sunday
      const daysSinceMonday = (dow + 6) % 7
      const from = addDays(asOf, -daysSinceMonday)
      return { from, to: addDays(from, 6) }
    }

    case 'this_month':
      return monthRange(asOf)

    case 'last_month':
      return monthRange(addMonths(asOf, -1))

    case 'this_quarter':
      return fiscalQuarterRange(fiscalYearStartYear(asOf), fiscalQuarterOf(asOf))

    case 'last_quarter': {
      // Step back a quarter by moving three months earlier and re-deriving,
      // which handles the Q1 → previous-FY-Q4 wrap for free.
      const previous = addMonths(asOf, -3)
      return fiscalQuarterRange(fiscalYearStartYear(previous), fiscalQuarterOf(previous))
    }

    case 'this_financial_year': {
      const fy = fiscalYearOf(asOf)
      return { from: fy.start, to: fy.end }
    }

    case 'last_financial_year': {
      const fy = fiscalYearFromStartYear(fiscalYearStartYear(asOf) - 1)
      return { from: fy.start, to: fy.end }
    }

    case 'all_time':
      return { from: MIN_DATE, to: asOf }
  }
}

/** Formats an `IsoDate` for display: "2026-07-20" → "20-07-2026" (DD-MM-YYYY). */
export function formatIsoDate(date: IsoDate): string {
  const { year, month, day } = parseIsoDate(date)
  // DD-MM-YYYY — the numeric day-first form Indian users read on every voucher.
  return `${pad2(day)}-${pad2(month)}-${year}`
}

/** Formats a range the way report headers and filter chips show it. */
export function formatDateRange(range: DateRange): string {
  if (range.from === MIN_DATE) return `Up to ${formatIsoDate(range.to)}`
  if (range.from === range.to) return formatIsoDate(range.from)
  return `${formatIsoDate(range.from)} to ${formatIsoDate(range.to)}`
}
